import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';

import { quatFromArray, vec3FromArray } from './packing';

/**
 * Smooths poses that arrive 15 times a second into something worth rendering at 72.
 *
 * Rendering each pose the moment it lands would show other people juddering, because the
 * gaps between packets are uneven and larger than a frame. Instead the clock is deliberately
 * held slightly in the past, so there are almost always two samples to sit between, and the
 * motion is read from them rather than guessed.
 */

/**
 * How far behind the newest sample to render. Slightly more than one send interval at 15 Hz,
 * which buys a spare sample against jitter at a latency nobody notices in a shared room.
 */
const RENDER_DELAY_MS = 120;

/** Enough history to cover a stall without growing without bound. */
const MAX_SAMPLES = 12;

/** Beyond this, motion was a teleport rather than a movement, so snap instead of sliding. */
const SNAP_DISTANCE = 1.5;

interface Sample {
  t: number;
  position: Vector3;
  rotation: Quaternion;
}

/** One interpolated thing — a head, or a hand. */
export class PoseTrack {
  private readonly samples: Sample[] = [];

  /** Latest known state, whether or not it is being rendered yet. */
  readonly position = Vector3.Zero();
  readonly rotation = Quaternion.Identity();

  /** False until a sample has arrived, so nothing renders at the origin. */
  primed = false;

  push(t: number, p: number[], r: number[]): void {
    // Exact-length, matching what vec3FromArray/quatFromArray below actually require and throw
    // on otherwise — see RoomView.applyLiveTransform's identical fix and comment
    // (plan/open-items.md, "`length < 3` guards claim a robustness they do not deliver").
    if (p.length !== 3 || r.length !== 4) {
      return;
    }

    const sample: Sample = {
      t,
      position: vec3FromArray(p),
      rotation: quatFromArray(r),
    };

    // Out-of-order arrivals are dropped rather than sorted in: they are older than what is
    // already being rendered, so they have nothing to contribute.
    const newest = this.samples[this.samples.length - 1];

    if (newest !== undefined && sample.t <= newest.t) {
      return;
    }

    this.samples.push(sample);

    if (this.samples.length > MAX_SAMPLES) {
      this.samples.shift();
    }

    if (!this.primed) {
      this.primed = true;
      this.position.copyFrom(sample.position);
      this.rotation.copyFrom(sample.rotation);
    }
  }

  /**
   * Advances to the given wall-clock time. `nowMs` is on the *sender's* clock scale, since
   * samples carry sender timestamps — see `AvatarManager` for how that is aligned.
   */
  sample(nowMs: number): void {
    if (this.samples.length === 0) {
      return;
    }

    const target = nowMs - RENDER_DELAY_MS;

    let before: Sample | undefined;
    let after: Sample | undefined;

    for (const sample of this.samples) {
      if (sample.t <= target) {
        before = sample;
      } else {
        after = sample;
        break;
      }
    }

    // Nothing old enough yet: sit on the earliest known pose rather than at the origin.
    if (before === undefined) {
      const earliest = this.samples[0]!;
      this.position.copyFrom(earliest.position);
      this.rotation.copyFrom(earliest.rotation);
      return;
    }

    // Starved — the sender stopped, or packets are late. Hold the last known pose. Notably
    // *not* extrapolated: a guess would drift a person through a wall, and in a room where
    // people reach for the same objects a wrong position is worse than a stale one.
    if (after === undefined) {
      this.position.copyFrom(before.position);
      this.rotation.copyFrom(before.rotation);
      return;
    }

    const span = after.t - before.t;
    const alpha = span <= 0 ? 1 : Math.min(1, Math.max(0, (target - before.t) / span));

    if (Vector3.Distance(before.position, after.position) > SNAP_DISTANCE) {
      this.position.copyFrom(after.position);
      this.rotation.copyFrom(after.rotation);
      return;
    }

    Vector3.LerpToRef(before.position, after.position, alpha, this.position);
    Quaternion.SlerpToRef(before.rotation, after.rotation, alpha, this.rotation);
  }

  /** Milliseconds since the newest sample, for spotting a peer that went quiet. */
  stalenessAt(nowMs: number): number {
    const newest = this.samples[this.samples.length - 1];
    return newest === undefined ? Number.POSITIVE_INFINITY : nowMs - newest.t;
  }

  clear(): void {
    this.samples.length = 0;
    this.primed = false;
  }
}
